import { v4 as uuid } from "uuid";

import { Thumbnail } from "./Thumbnail";
import { readDicomFile } from "./../commons/dicom";
import { stringOrEmpty } from "./../commons/utils";
import { Target, createTarget } from "./Target";
import { User, createUser } from "./User";
import { Request, createRequest } from "./Request";
import {
  UploadStack,
  getUploadStackThumbnail,
  createUploadStack,
  updateUploadStack,
} from "./UploadStack";
import { Patient, createPatient, updatePatient } from "./Patient";

export interface Upload {
  user: User;
  target: Target;
  request: Request;
  patient?: Patient;
  stacks: UploadStack[];
  termsOfUse: boolean;
}

export const createUpload = (): Upload => ({
  user: createUser(),
  target: createTarget(),
  request: createRequest(),
  patient: undefined,
  stacks: [],
  termsOfUse: false,
});

export const uploadToFormData = (upload: Upload): FormData => {
  let formData = new FormData();

  // JSON
  formData.append("user", JSON.stringify(upload.user));
  formData.append("target", JSON.stringify(upload.target));
  formData.append("request", JSON.stringify(upload.request));
  formData.append("patient", JSON.stringify(upload.patient));
  formData.append("stacks", JSON.stringify(upload.stacks));

  // files (ID is key)
  for (let stack of upload.stacks)
    for (let file of stack.files) {
      // generate special uuids (avoid PHP issues with dots in keys)
      formData.append(file.id, file);
    }

  // done
  return formData;
};

export const addUploadFiles = async (upload: Upload, files: File[]) => {
  return handleUploadFiles(upload, files);
};

export const removeUploadFiles = (upload: Upload, id: string) => {
  removeStack(upload, id);

  // reset patient
  if (upload.stacks.length === 0)
    upload.patient = undefined;
};

export const getUploadThumbnails = (upload: Upload, wado: any): Thumbnail[] => {
  let result: Thumbnail[] = [];

  // loop through all stacks
  upload.stacks.forEach((stack) => {
    const thumbnail = getUploadStackThumbnail(stack, wado, upload.patient);
    if (thumbnail !== undefined) {
      // add to array
      result.push(thumbnail);
    }
  });
  return result;
};

// return file count in all upload stacks
export const getUploadCount = (upload: Upload): number => {
  let count = 0;
  for (const stack of upload.stacks) {
    count += stack.files.length;
  }
  return count;
};

// return estimated total size of upload
export const getUploadTotal = (upload: Upload): number => {
  let total = 0;

  // total size of all files
  for (const stack of upload.stacks) {
    for (const file of stack.files) {
      total += file.size;
    }
  }

  // estimated size of data
  total += JSON.stringify(upload.user).length;
  total += JSON.stringify(upload.target).length;
  total += JSON.stringify(upload.request).length;
  total += JSON.stringify(upload.stacks).length;

  // done
  return total;
};

/////////////////////////////////////////////////////////////

const handleUploadFiles = async (upload: Upload, files: File[]) => {
  // add all files (asynchronously)
  return Promise.all(files.map(async (file) => handleUploadFile(upload, file)));
};

const handleUploadFile = async (upload: Upload, file: File) => {
  // handle file
  try {
    // try as dicom, first
    const dataset = await readDicomFile(file);
    if (dataset !== undefined)
      return await handleUploadFileDicom(upload, file, dataset);
    return await handleUploadFileMedia(upload, file);
  } catch (e) {
    console.error("Exception: ", e);
    throw e;
  }
};

const handleUploadFileDicom = async (
  upload: Upload,
  file: File,
  dataset: any
) => {
  // Series Instance UID (required)
  const seriesuid: string = stringOrEmpty(dataset.string("x0020000e"));
  if (seriesuid.length === 0)
    throw new Error("invalid DICOM Object: missing Series Instance UID");

  // Study Instance UID (optional)
  const studyuid: string = stringOrEmpty(dataset.string("x0020000d"));

  // find stack/series for uid
  let index = upload.stacks.findIndex((stack) => stack.id === seriesuid);
  if (index === -1) {
    // not found ... add new stack
    index =
      upload.stacks.push(
        createUploadStack(seriesuid, "application/dicom", true, studyuid)
      ) - 1;
  }

  // add file to series (stack)
  const instid = updateUploadStack(upload.stacks[index], file, dataset);

  // update patient/study
  if (!upload.patient)
    upload.patient = createPatient();
  updatePatient(dataset, upload.patient);

  // done
  return instid;
};

const handleUploadFileMedia = async (upload: Upload, file: File) => {
  // date from file's modified date
  const date: Date = new Date(file.lastModified);

  // new stack for each media file
  const stack = createUploadStack(uuid(), file.type, false, "", "OT", date);
  const id = uuid();
  stack.files.push(Object.assign(file, { id: id, inst: "" }));
  upload.stacks.push(stack);

  // update patient/study
  if (!upload.patient)
    upload.patient = createPatient();

  // done
  return id;
};

const removeStack = (upload: Upload, id: string) => {
  upload.stacks = upload.stacks.filter((stack) => stack.id !== id);
};
